import { Vec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { StringUtil } from "@base/util/StringUtil";
import { IStandardTemplate, StandardTemplateId, StandardTemplateShape } from "@biz/definitions/SunSailDefinitions";

export const standardTemplateArray: IStandardTemplate[] = [
  { id: "3X2", shape: "rect", w: 3, h: 2 },
  { id: "3X3", shape: "rect", w: 3, h: 3 },
  { id: "3X4", shape: "rect", w: 3, h: 4 },
  { id: "3X5", shape: "rect", w: 3, h: 5 },
  { id: "3X6", shape: "rect", w: 3, h: 6 },
  { id: "4X4", shape: "rect", w: 4, h: 4, },
  { id: "4X5", shape: "rect", w: 4, h: 5, },
  { id: "4X6", shape: "rect", w: 4, h: 6, },

  { id: "4X2X3X2", shape: "trapezoid", a: 4, b: 2, c: 3, d: 2 },
  { id: "4X3X3X3", shape: "trapezoid", a: 4, b: 3, c: 3, d: 3 },
  { id: "5X3X4X3", shape: "trapezoid", a: 5, b: 3, c: 4, d: 3 },
  { id: "5X4X4X4", shape: "trapezoid", a: 5, b: 4, c: 4, d: 4 },
  { id: "6X4X5X4", shape: "trapezoid", a: 6, b: 4, c: 5, d: 4 },

  { id: "3ECK01", shape: "triangle", a: 3.3, b: 3.3, c: 3.3, },
  { id: "3ECK02", shape: "triangle", a: 3.6, b: 3.6, c: 3.6, },
  { id: "3ECK03", shape: "triangle", a: 3.8, b: 3.8, c: 3.8, },
  { id: "3ECK04", shape: "triangle", a: 4.0, b: 4.0, c: 4.5, },
  { id: "3ECK05", shape: "triangle", a: 5.0, b: 5.0, c: 4.0, },
  { id: "3ECK06", shape: "triangle", a: 5.0, b: 5.0, c: 5.0, },
  { id: "3ECK07", shape: "triangle", a: 5.0, b: 5.0, c: 6.0, },
  { id: "3ECK08", shape: "triangle", a: 5.9, b: 5.9, c: 5.9, },
  { id: "3ECK09", shape: "triangle", a: 2.5, b: 2.5, c: 3.5, },
  { id: "3ECK10", shape: "triangle", a: 3.0, b: 3.0, c: 4.2, },
  { id: "3ECK11", shape: "triangle", a: 3.6, b: 3.6, c: 5.0, },
  { id: "3ECK12", shape: "triangle", a: 4.0, b: 4.0, c: 5.6, },
  { id: "3ECK13", shape: "triangle", a: 4.5, b: 4.5, c: 6.3, },
  { id: "3ECK14", shape: "triangle", a: 5.0, b: 5.0, c: 7.0, },
]

const standardTemplates: Record<string, IStandardTemplate> = {};
for (const s of standardTemplateArray) {
  standardTemplates[s.id] = s;
}

function calcTriangleArea(a: number, b: number, c: number): number {
  const s = (a + b + c) / 2;
  const area = Math.sqrt(s * (s - a) * (s - b) * (s - c));
  return area;
}

export class StandardTemplate {
  static getArea(templateId: StandardTemplateId | undefined): number {
    const template = StandardTemplate.getTemplate(templateId);
    if (!template)
      return 0;
    switch (template.shape) {
      case "rect":
        return template.w * template.h;
      case "triangle":
        return calcTriangleArea(template.a, template.b, template.c);
      case "trapezoid": {
        const x = (template.a - template.c) * 0.5;
        const h = Math.sqrt(template.b * template.b - x * x);
        return (template.a + template.c) * 0.5 * h;
      }
    }
    return 0;
  }

  static getShapes(): StandardTemplateShape[] {
    return [
      "triangle",
      "rect",
      "trapezoid"
    ]
  }

  static getTemplatePoleCount(shape: StandardTemplateShape): number {
    switch (shape) {
      case "triangle":
        return 3;
      case "rect":
        return 4;
      case "trapezoid":
        return 4;
    }
  }

  static getTemplate(sizeId: StandardTemplateId | undefined): IStandardTemplate | undefined {
    if (!sizeId)
      return undefined;
    return standardTemplates[sizeId];
  }

  static getSizeText(sizeId: StandardTemplateId): string {
    const template = StandardTemplate.getTemplate(sizeId);
    if (!template)
      return sizeId;
    if (template.shape === "triangle") {
      return `${template.a}x${template.b}x${template.c}`
    }
    return StringUtil.replaceAll(sizeId, "X", "x");
  }

  static getDefaultStandardSize(poleCount: number): IStandardTemplate {
    if (poleCount === 3)
      return standardTemplates["3ECK06"];
    else
      return standardTemplates["4X5"];
  }

  static getTemplatesForShape(shape: StandardTemplateShape): IStandardTemplate[] {
    return standardTemplateArray.filter((s) => s.shape === shape)
  }

  static calcStandardTemplatePositions(standardTemplate: IStandardTemplate): IVec3[] {
    const r: IVec3[] = [];
    switch (standardTemplate.shape) {
      case "triangle": {
        const { a, b, c } = standardTemplate;
        const alpha = Math.acos((c * c - b * b - a * a) / (-2 * b * a))
        const result = Vec2.rotate({ x: b, y: 0 }, alpha);
        r[0] = Vec3.from(0, 0, 0);
        r[1] = Vec3.from(result.y, 0, -result.x); // https://plaspack.atlassian.net/browse/APPSK-149
        r[2] = Vec3.from(0, 0, -a);

        const center = Vec3.center(r);
        for (let i = 0; i < r.length; i++)
          r[i] = Vec3.sub(r[i], center);

        return r;
      }
      case "rect": {
        const w = standardTemplate.w * 0.5;
        const h = standardTemplate.h * 0.5;
        r[0] = Vec3.from(w, 0, h);
        r[1] = Vec3.from(-w, 0, h);
        r[2] = Vec3.from(-w, 0, -h);
        r[3] = Vec3.from(w, 0, -h);
        return r;
      }

      case "trapezoid": {
        const x = (standardTemplate.a - standardTemplate.c) * 0.5;
        const h2 = Math.sqrt(standardTemplate.b * standardTemplate.b - x * x) * 0.5;
        const c2 = standardTemplate.c * 0.5;
        const a2 = standardTemplate.a * 0.5;

        r[0] = Vec3.from(-a2, 0.0, -h2);
        r[1] = Vec3.from(-c2, 0.0, h2);
        r[2] = Vec3.from(c2, 0.0, h2);
        r[3] = Vec3.from(a2, 0.0, -h2);

        r[0] = Vec3.from(h2, 0.0, a2);
        r[1] = Vec3.from(-h2, 0.0, c2);
        r[2] = Vec3.from(-h2, 0.0, -c2);
        r[3] = Vec3.from(h2, 0.0, -a2);

        return r;
      }
    }
  }
}